/**
 * Role-Based Access Control Configuration
 * 
 * Defines which roles can access which routes in the admin panel.
 * 
 * Roles:
 * - ADMIN: Full access to all pages
 * - FACILITY_OWNER: Access to Dashboard, Analytics, Clubs, Facilities, Bookings, Sessions, Payments, Matches, Managers
 * - MANAGER: Access to Dashboard, Clubs, Facilities, Bookings, Sessions, Matches, Payments
 */

// Define all available roles
export const ROLES = {
    ADMIN: 'ADMIN',
    FACILITY_OWNER: 'FACILITY_OWNER',
    MANAGER: 'MANAGER',
};

// Routes that are accessible by all authenticated users
const PUBLIC_DASHBOARD_ROUTES = [
    '/dashboard',
];

// Route permissions configuration
// Each key is a route pattern, value is an array of roles that can access it
export const ROUTE_PERMISSIONS = {
    // Dashboard - All roles
    '/dashboard': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],
    '/dashboard/analytics': [ROLES.ADMIN, ROLES.FACILITY_OWNER],

    // Users - Admin only
    '/dashboard/users': [ROLES.ADMIN],

    // Clubs - All roles
    '/dashboard/clubs': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Facilities - All roles
    '/dashboard/facilities': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Bookings - All roles
    '/dashboard/bookings': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Sessions - All roles
    '/dashboard/sessions': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Matches - All roles
    '/dashboard/matches': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Open Play board - All roles (the backend authorizes per session/facility)
    '/dashboard/open-play': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Payments - All roles
    '/dashboard/payments': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],
    '/dashboard/wallet': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],
    '/dashboard/vouchers': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Leaderboard - Admin only
    '/dashboard/leaderboard': [ROLES.ADMIN],

    // Banners - Admin only
    '/dashboard/banners': [ROLES.ADMIN],

    // App Versions - Admin only
    '/dashboard/app-versions': [ROLES.ADMIN],

    // Managers - Admin and Facility Owner
    '/dashboard/admin/managers': [ROLES.ADMIN, ROLES.FACILITY_OWNER],

    // Facility Owners - Admin only
    '/dashboard/admin/facility-owners': [ROLES.ADMIN],

    // Help Desk - Admin only
    '/dashboard/feedback': [ROLES.ADMIN],
    '/dashboard/support': [ROLES.ADMIN],
    '/dashboard/bugs': [ROLES.ADMIN],

    // Chat - All roles
    '/dashboard/chat': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Monitors - All roles (the backend scopes monitors to the caller's facilities)
    '/dashboard/monitors': [ROLES.ADMIN, ROLES.FACILITY_OWNER, ROLES.MANAGER],

    // Display Ads - Admin only (ads/playlists/schedules are global, not facility-scoped)
    '/dashboard/ads': [ROLES.ADMIN],

    // Reports - Admin only
    '/dashboard/reports': [ROLES.ADMIN],

    // Audit Logs - Admin only
    '/dashboard/audit-logs': [ROLES.ADMIN],

    // Settings - Admin only
    '/dashboard/settings': [ROLES.ADMIN],

    // Permissions - Admin only
    '/dashboard/permissions': [ROLES.ADMIN],
};

/**
 * Check if a role has access to a specific path
 * @param {string} path - The current pathname
 * @param {string} role - The user's role (case-insensitive)
 * @returns {boolean} - Whether the user has access
 */
export function hasRouteAccess(path, role) {
    if (!role) return false;

    const normalizedRole = role.toUpperCase();

    // Admin has access to everything
    if (normalizedRole === ROLES.ADMIN) {
        return true;
    }

    // Check exact match first
    if (ROUTE_PERMISSIONS[path]) {
        return ROUTE_PERMISSIONS[path].includes(normalizedRole);
    }

    // Check for pattern matches (for dynamic routes like /dashboard/users/123)
    const pathSegments = path.split('/').filter(Boolean);

    // Build progressively shorter paths to find a match
    for (let i = pathSegments.length; i > 0; i--) {
        const partialPath = '/' + pathSegments.slice(0, i).join('/');
        if (ROUTE_PERMISSIONS[partialPath]) {
            return ROUTE_PERMISSIONS[partialPath].includes(normalizedRole);
        }
    }

    // If no specific permission found, deny access for non-admin
    // This ensures new routes are protected by default
    return false;
}

/**
 * Get the default redirect path for a role when access is denied
 * @param {string} role - The user's role
 * @returns {string} - The redirect path
 */
export function getDefaultRedirect(role) {
    const normalizedRole = role?.toUpperCase();

    switch (normalizedRole) {
        case ROLES.FACILITY_OWNER:
        case ROLES.MANAGER:
            return '/dashboard';
        default:
            return '/dashboard';
    }
}

/**
 * Get all accessible routes for a role
 * @param {string} role - The user's role
 * @returns {string[]} - Array of accessible route patterns
 */
export function getAccessibleRoutes(role) {
    if (!role) return [];
    const normalizedRole = role.toUpperCase();

    // Admin has access to all routes
    if (normalizedRole === ROLES.ADMIN) {
        return Object.keys(ROUTE_PERMISSIONS);
    }

    return Object.entries(ROUTE_PERMISSIONS)
        .filter(([_, roles]) => roles.includes(normalizedRole))
        .map(([route]) => route);
}
